import { zodResolver } from '@hookform/resolvers/zod';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Plug, RefreshCw, Unplug } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import { IntegrationEnabledBadge } from '@/Domain/components/Integration/IntegrationEnabledBadge';
import { IntegrationStatusBadge } from '@/Domain/components/Integration/IntegrationStatusBadge';
import { IntegrationConnectionCard } from '@/Domain/components/Integration/components/IntegrationConnectionCard';
import { IntegrationIntroduction } from '@/Domain/components/Integration/components/IntegrationIntroduction';
import { IntegrationSection } from '@/Domain/components/Integration/components/IntegrationSection';
import { IntegrationToggleField } from '@/Domain/components/Integration/components/IntegrationToggleField';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { SlackIcon } from '@/Domain/components/icons/Slack.icon';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import {
  trackIntegrationConnectFailed,
  trackIntegrationConnectStarted,
} from '@/Domain/hooks/Integration/integrationConnectAnalytics';

const INTEGRATION_NAME = 'slackv2' as const;
const QUERY_KEY = [QueryKey.integration.get, INTEGRATION_NAME] as const;

export interface SlackV2Params {
  slackv2_bot_token?: string;
  slackv2_bot_user_id?: string;
  slackv2_team_id?: string;
  slackv2_team_name?: string;
  slackv2_bot_active?: boolean;
  slackv2_reply_thread?: boolean;
  slackv2_reply_channel?: boolean;
}

interface IntegrationResponse {
  name: string;
  params: SlackV2Params;
}

const formSchema = z.object({
  slackv2_reply_thread: z.boolean(),
  slackv2_reply_channel: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

export interface SlackV2SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SlackV2Sheet = ({ open, onOpenChange }: SlackV2SheetProps) => {
  const { t } = useTranslation(['integration']);
  const { capture } = usePostHogAnalytics();
  const queryClient = useQueryClient();
  const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState(false);

  const { data } = useQuery<IntegrationResponse>({
    queryKey: QUERY_KEY,
    enabled: open,
  });

  const params = data?.params ?? {};
  const isConnected = Boolean(params.slackv2_bot_token);
  const teamName = params.slackv2_team_name ?? '';
  const teamId = params.slackv2_team_id ?? '';
  const botUserId = params.slackv2_bot_user_id ?? '';

  const defaultValues = useMemo<FormValues>(
    () => ({
      slackv2_reply_thread: Boolean(params.slackv2_reply_thread),
      slackv2_reply_channel: Boolean(params.slackv2_reply_channel),
    }),
    [params.slackv2_reply_thread, params.slackv2_reply_channel],
  );

  const { control, reset } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  // Re-sync form when server data arrives / changes. `reset` clears dirty flags
  // so only user-initiated edits mark fields as changed.
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (!open) {
      setConfirmDisconnectOpen(false);
    }
  }, [open]);

  // The enable/disable toggle is instant (decoupled from the settings form), so
  // its state comes straight from the persisted params, not form state.
  const botActive = Boolean(params.slackv2_bot_active);

  const oauthMutation = useMutation<{ url: string }>({
    mutationKey: [QueryKey.integration.slackV2Oauth],
    onSuccess: (result) => {
      if (!result?.url) {
        trackIntegrationConnectFailed(capture, 'slack', 'oauth_failed');
        toast.error(t('integration:slackV2.oauth_failed'));
        return;
      }
      window.location.href = result.url;
    },
    onError: () => {
      trackIntegrationConnectFailed(capture, 'slack', 'oauth_failed');
      toast.error(t('integration:slackV2.oauth_failed'));
    },
  });

  const patchMutation = useMutation<
    IntegrationResponse,
    AxiosError,
    { name: typeof INTEGRATION_NAME; params: Partial<FormValues> }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: (response) => {
      queryClient.setQueryData<IntegrationResponse>(QUERY_KEY, response);
      reset({
        slackv2_reply_thread: Boolean(response.params.slackv2_reply_thread),
        slackv2_reply_channel: Boolean(response.params.slackv2_reply_channel),
      });
      toast.success(t('integration:slackV2.saved'));
    },
    onError: () => {
      toast.error(t('integration:slackV2.save_failed'));
    },
  });

  const revokeMutation = useMutation({
    mutationKey: [QueryKey.integration.slackV2Revoke],
    onSuccess: () => {
      setConfirmDisconnectOpen(false);
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: () => {
      toast.error(t('integration:slackV2.revoke_failed'));
    },
  });

  // Instant enable/disable toggle, decoupled from the settings Save: shows a
  // standardized "<name> integration enabled/disabled" toast and never closes.
  const toggleActiveMutation = useMutation<
    IntegrationResponse,
    AxiosError,
    { name: typeof INTEGRATION_NAME; params: { slackv2_bot_active: boolean } }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: (response, variables) => {
      queryClient.setQueryData<IntegrationResponse>(QUERY_KEY, (prev) =>
        prev ? { ...prev, params: { ...prev.params, ...response.params } } : response,
      );
      const name = t('integration:slackV2.title');
      toast.success(
        variables.params.slackv2_bot_active
          ? t('integrations:toast.enabled', { name })
          : t('integrations:toast.disabled', { name }),
      );
    },
    onError: () => {
      toast.error(t('integration:slackV2.save_failed'));
    },
  });

  const handleToggleActive = (checked: boolean) => {
    toggleActiveMutation.mutate({
      name: INTEGRATION_NAME,
      params: { slackv2_bot_active: checked },
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="max-w-xl w-full h-full pb-0"
        data-testid="slackv2-sheet"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          onOpenChange(false);
        }}
      >
        <SheetHeader className="px-0.5 items-center flex-row">
          <div className="flex items-center gap-3">
            <SlackIcon className="size-8 shrink-0" />
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <SheetTitle data-testid="slackv2-sheet-title">
                  {t('integration:slackV2.title')}
                </SheetTitle>
                <IntegrationStatusBadge connected={isConnected} />
                <IntegrationEnabledBadge enabled={Boolean(params.slackv2_bot_active)} />
              </div>
            </div>
          </div>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            data-testid="slackv2-close-button"
            aria-label="Close"
            className="shrink-0"
          >
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <div className="overflow-y-auto px-0.5 pb-6 space-y-6 flex-1">
          <IntegrationIntroduction
            description={t('integration:slackV2.overview')}
            prerequisites={
              t('integration:slackV2.prerequisites', { returnObjects: true }) as string[]
            }
            docsHref="https://docs.quickchat.ai/channels/slack"
          />

          <IntegrationSection
            title={t('integrations:section.connect')}
            data-testid="slackv2-connect-enable-section"
          >
            {isConnected ? (
              <IntegrationConnectionCard
                data-testid="slackv2-connection-card"
                details={[
                  {
                    label: t('integration:slackV2.workspace'),
                    value: teamName,
                    'data-testid': 'slackv2-team-name',
                  },
                  {
                    label: t('integration:slackV2.team_id'),
                    value: teamId,
                    'data-testid': 'slackv2-team-id',
                  },
                  {
                    label: t('integration:slackV2.bot_user_id'),
                    value: botUserId,
                    'data-testid': 'slackv2-bot-user-id',
                  },
                ]}
                actions={
                  <>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="flex-1"
                      onClick={() => {
                        capture(PostHogEventEnum.SlackIntegrationAuthenticateClicked);
                        trackIntegrationConnectStarted(capture, 'slack');
                        oauthMutation.mutate();
                      }}
                      disabled={oauthMutation.isPending}
                      data-testid="slackv2-reconnect-button"
                    >
                      <RefreshCw className="size-4" />
                      {t('integrations:reconnect')}
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      className="flex-1"
                      onClick={() => setConfirmDisconnectOpen(true)}
                      disabled={revokeMutation.isPending}
                      data-testid="slackv2-disconnect-button"
                    >
                      <Unplug className="size-4" />
                      {t('integrations:disconnect')}
                    </Button>
                  </>
                }
              />
            ) : (
              <Button
                type="button"
                variant="dark"
                size="sm"
                className="w-full"
                onClick={() => {
                  capture(PostHogEventEnum.SlackIntegrationAuthenticateClicked);
                  trackIntegrationConnectStarted(capture, 'slack');
                  oauthMutation.mutate();
                }}
                disabled={oauthMutation.isPending}
                data-testid="slackv2-connect-button"
              >
                <Plug className="size-4" />
                {oauthMutation.isPending
                  ? t('integration:slackV2.connecting')
                  : t('integrations:connect', { name: 'Slack' })}
              </Button>
            )}
          </IntegrationSection>

          <IntegrationSection
            title={t('integrations:section.settings')}
            data-testid="slackv2-settings-section"
          >
            <div className="space-y-4" data-testid="slackv2-settings-form">
              <IntegrationToggleField
                id="slackv2-bot-active"
                label={t('integrations:enable', { name: 'Slack' })}
                checked={botActive}
                onCheckedChange={handleToggleActive}
                disabled={!isConnected || toggleActiveMutation.isPending}
                data-testid="slackv2-bot-active-toggle"
              />

              <Controller
                control={control}
                name="slackv2_reply_thread"
                render={({ field }) => (
                  <IntegrationToggleField
                    id="slackv2-reply-thread"
                    label={t('integration:slackV2.reply_thread.label')}
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                      patchMutation.mutate({
                        name: INTEGRATION_NAME,
                        params: { slackv2_reply_thread: checked },
                      });
                    }}
                    disabled={!isConnected || !botActive}
                    data-testid="slackv2-reply-thread-toggle"
                  />
                )}
              />

              <Controller
                control={control}
                name="slackv2_reply_channel"
                render={({ field }) => (
                  <IntegrationToggleField
                    id="slackv2-reply-channel"
                    label={t('integration:slackV2.reply_channel.label')}
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                      patchMutation.mutate({
                        name: INTEGRATION_NAME,
                        params: { slackv2_reply_channel: checked },
                      });
                    }}
                    disabled={!isConnected || !botActive}
                    data-testid="slackv2-reply-channel-toggle"
                  />
                )}
              />
            </div>
          </IntegrationSection>
        </div>

        <AlertDialogPrimitive.Root
          open={confirmDisconnectOpen}
          onOpenChange={setConfirmDisconnectOpen}
        >
          <AlertDialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 backdrop-blur-sm z-20" />
          <AlertDialogPrimitive.Content
            className="absolute inset-0 flex items-center justify-center p-4 z-30 focus:outline-none"
            data-testid="slackv2-disconnect-dialog"
          >
            <div className="bg-card rounded-lg p-6 max-w-md w-full shadow-lg space-y-4">
              <AlertDialogPrimitive.Title className="text-lg font-semibold text-foreground">
                {t('integration:slackV2.disconnect_confirm.title')}
              </AlertDialogPrimitive.Title>
              <AlertDialogPrimitive.Description className="text-sm text-muted-foreground">
                {t('integration:slackV2.disconnect_confirm.description')}
              </AlertDialogPrimitive.Description>
              <div className="flex items-center justify-end gap-2 pt-2">
                <AlertDialogPrimitive.Cancel asChild>
                  <Button variant="secondary" size="md" data-testid="slackv2-disconnect-cancel">
                    {t('integration:slackV2.disconnect_confirm.cancel')}
                  </Button>
                </AlertDialogPrimitive.Cancel>
                <Button
                  variant="danger"
                  size="md"
                  onClick={() => revokeMutation.mutate()}
                  disabled={revokeMutation.isPending}
                  data-testid="slackv2-disconnect-confirm"
                >
                  {t('integration:slackV2.disconnect_confirm.confirm')}
                </Button>
              </div>
            </div>
          </AlertDialogPrimitive.Content>
        </AlertDialogPrimitive.Root>
      </SheetContent>
    </Sheet>
  );
};

export default SlackV2Sheet;
